import { Container, Graphics, Text, TextStyle, Assets, Sprite } from 'pixi.js';
import { COLORS } from '../config/Colors';
import { GAME_CONFIG, getAllSymbols } from '../config/GameConfig';

type TabType = 'howToPlay' | 'paytable';

export class InfoPopup extends Container {
  private background: Graphics;
  private contentContainer: Container;
  private tabContainer: Container;
  private tabGraphics: Graphics[];
  private tabTexts: Text[];
  private currentTab: TabType = 'howToPlay';
  private isOpen: boolean = false;
  private paylinesSprite: Sprite | null = null;
  
  private readonly POPUP_WIDTH = 650;
  private readonly POPUP_HEIGHT = 850;
  
  private onCloseCallback?: () => void;
  
  constructor() {
    super();
    
    this.tabGraphics = [];
    this.tabTexts = [];
    this.background = new Graphics();
    this.contentContainer = new Container();
    this.tabContainer = new Container();
    
    this.drawBackground();
    this.createTabs();
    this.createCloseButton();
    this.showHowToPlay();
    
    this.visible = false;
    this.zIndex = 200;
  }
  
  private drawBackground(): void {
    // Semi-transparent overlay
    this.background.rect(-384, -512, 768, 1024);
    this.background.fill({ color: 0x000000, alpha: 0.7 });
    
    // Main popup background - traditional parchment color
    this.background.roundRect(-this.POPUP_WIDTH/2, -this.POPUP_HEIGHT/2, this.POPUP_WIDTH, this.POPUP_HEIGHT, 20);
    this.background.fill({ color: 0xFFF8DC }); // Cornsilk (light parchment)
    this.background.stroke({ color: 0xDAA520, width: 4 }); // Goldenrod border
    
    this.addChild(this.background);
    
    // Tab container
    this.tabContainer.y = -this.POPUP_HEIGHT/2 + 50;
    this.addChild(this.tabContainer);
    
    this.addChild(this.contentContainer);
  }
  
  private createTabs(): void {
    const tabWidth = 280;
    const tabHeight = 50;
    
    // Tab background bar - light brown
    const tabBar = new Graphics();
    tabBar.rect(-325, -10, 650, tabHeight + 10);
    tabBar.fill({ color: 0xF5E6D3 }); // Light parchment
    this.tabContainer.addChild(tabBar);
    
    // "How to Play" tab
    const tab1 = this.createTab('How to Play', -160, tabWidth, tabHeight, true);
    this.tabContainer.addChild(tab1);
    this.tabGraphics.push(tab1);
    
    // "Paytable" tab
    const tab2 = this.createTab('Paytable', 160, tabWidth, tabHeight, false);
    this.tabContainer.addChild(tab2);
    this.tabGraphics.push(tab2);
  }
  
  private createTab(text: string, x: number, width: number, height: number, isActive: boolean): Graphics {
    const tab = new Graphics();
    const activeColor = 0x8B4513;   // Saddle brown (active)
    const inactiveColor = 0xD2691E; // Chocolate (inactive)
    
    // Tab background
    tab.roundRect(-width/2, 0, width, height, 10);
    tab.fill({ color: isActive ? activeColor : inactiveColor });
    
    if (isActive) {
      // Active tab underline - gold
      tab.rect(-width/2, height - 4, width, 4);
      tab.fill({ color: 0xFFD700 }); // Gold
    }
    
    // Tab text - centered within the tab
    const textObj = new Text({
      text: text,
      style: new TextStyle({
        fontFamily: 'Spenbeb, Georgia, serif',
        fontSize: 18,
        fontWeight: 'bold',
        fill: isActive ? 0xFFD700 : 0xFFF8DC,  // Gold (active) or light parchment (inactive)
        stroke: isActive ? { color: 0x4A3728, width: 1 } : undefined  // Dark brown outline for active
      })
    });
    textObj.anchor.set(0.5);
    textObj.x = 0; // Center relative to tab
    textObj.y = height / 2; // Center vertically
    
    tab.addChild(textObj);
    tab.x = x; // Position the entire tab
    this.tabTexts.push(textObj);
    
    // Make tab interactive
    tab.eventMode = 'static';
    tab.cursor = 'pointer';
    tab.on('pointerdown', () => {
      const tabIndex = this.tabGraphics.indexOf(tab);
      if (tabIndex === 0) {
        this.switchTab('howToPlay');
      } else {
        this.switchTab('paytable');
      }
    });
    
    return tab;
  }
  
  private createCloseButton(): void {
    const closeBtn = new Graphics();
    
    // Circle background - traditional brown
    closeBtn.circle(0, 0, 20);
    closeBtn.fill({ color: 0x8B4513 }); // Saddle brown
    closeBtn.stroke({ color: 0xFFD700, width: 2 }); // Gold border
    
    // X symbol - gold
    const xText = new Text({
      text: '✕',
      style: new TextStyle({
        fontFamily: 'Spenbeb, Georgia, serif',
        fontSize: 24,
        fontWeight: 'bold',
        fill: 0xFFD700 // Gold
      })
    });
    xText.anchor.set(0.5);
    closeBtn.addChild(xText);
    
    closeBtn.x = 290;
    closeBtn.y = -this.POPUP_HEIGHT/2 + 25;
    
    closeBtn.eventMode = 'static';
    closeBtn.cursor = 'pointer';
    closeBtn.on('pointerdown', () => this.close());
    
    this.addChild(closeBtn);
  }
  
  private async switchTab(tab: TabType): Promise<void> {
    this.currentTab = tab;
    
    // Update tab appearances
    const tabWidth = 280;
    const tabHeight = 50;
    const activeColor = 0x8B4513;    // Saddle brown (active)
    const inactiveColor = 0xD2691E;  // Chocolate (inactive)
    
    this.tabGraphics.forEach((tabGraphics, index) => {
      const isActive = (tab === 'howToPlay' && index === 0) || (tab === 'paytable' && index === 1);
      
      tabGraphics.clear();
      tabGraphics.roundRect(-tabWidth/2, 0, tabWidth, tabHeight, 10);
      tabGraphics.fill({ color: isActive ? activeColor : inactiveColor });
      
      if (isActive) {
        tabGraphics.rect(-tabWidth/2, tabHeight - 4, tabWidth, 4);
        tabGraphics.fill({ color: 0xFFD700 }); // Gold
      }
      
      if (this.tabTexts[index]) {
        this.tabTexts[index].style.fill = isActive ? 0xFFD700 : 0xFFF8DC; // Gold (active) or light parchment (inactive)
        tabGraphics.addChild(this.tabTexts[index]);
      }
    });
    
    // Clear and show new content
    this.contentContainer.removeChildren();
    
    if (tab === 'howToPlay') {
      this.showHowToPlay();
    } else {
      await this.showPaytable();
    }
  }
  
  private showHowToPlay(): void {
    const titleStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 22,
      fontWeight: 'bold',
      fill: 0x654321, // Dark brown
      stroke: { color: 0xFFF8DC, width: 2 } // Light parchment outline
    });
    
    const bodyStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 15,
      fill: 0x4A3728, // Dark brown
      wordWrap: true,
      wordWrapWidth: 600,
      leading: 6,
      lineHeight: 20
    });
    
    const sections = [
      { title: '🎰 How to Spin', content: '• Set bet amount using +/- buttons\n• Click SPIN to play\n• Match 3-5 symbols left-to-right\n• Win multiplier × bet = payout' },
      { title: '💰 Winning', content: '• 3 symbols = Small win\n• 4 symbols = Medium win\n• 5 symbols = Big win!\n• More paylines = more chances' },
      { title: '📊 Tips', content: '• Start with lower bets\n• Increase bet after wins\n• Use Auto-spin carefully' }
    ];
    
    let yOffset = -320;
    
    sections.forEach(section => {
      const title = new Text({ text: section.title, style: titleStyle });
      title.x = -280;
      title.y = yOffset;
      this.contentContainer.addChild(title);
      yOffset += 40;
      
      const lines = section.content.split('\n');
      lines.forEach((line: string) => {
        const text = new Text({ text: line, style: bodyStyle });
        text.x = -280;
        text.y = yOffset;
        this.contentContainer.addChild(text);
        yOffset += 22;
      });
      
      yOffset += 15;
    });
  }
  
  private async showPaytable(): Promise<void> {
    // Clear previous content
    this.contentContainer.removeChildren();
    
    const titleStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 16,
      fontWeight: 'bold',
      fill: COLORS.PRIMARY
    });
    
    const multiplierStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 11,
      fill: 0x4A3728
    });
    
    let yOffset = -200;
    
    // Load and show paylines image at top (compact)
    try {
      const paylinesTexture = await Assets.load('/assets/ui-2/paylines.png');
      this.paylinesSprite = new Sprite(paylinesTexture);
      
      // Scale to fit width (compact)
      const maxWidth = 450;
      const scale = (maxWidth / paylinesTexture.width) * 0.65;
      
      this.paylinesSprite.scale.set(scale);
      this.paylinesSprite.anchor.set(0.5);
      this.paylinesSprite.x = 0;
      this.paylinesSprite.y = yOffset;
      
      this.contentContainer.addChild(this.paylinesSprite);
      
      yOffset += (this.paylinesSprite.height / 2) + 20;
    } catch (error) {
      console.error('Failed to load paylines image:', error);
    }
    
    // Symbols section title
    const symbolsTitle = new Text({ text: 'SYMBOL PAYOUTS', style: titleStyle });
    symbolsTitle.anchor.set(0.5);
    symbolsTitle.x = 0;
    symbolsTitle.y = yOffset;
    this.contentContainer.addChild(symbolsTitle);
    yOffset += 25;
    
    // Table header
    const headerStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 11,
      fontWeight: 'bold',
      fill: 0xFFD700
    });
    
    const headerBg = new Graphics();
    headerBg.roundRect(-280, yOffset - 3, 560, 20, 4);
    headerBg.fill({ color: 0x5D4037, alpha: 0.8 });
    this.contentContainer.addChild(headerBg);
    
    const headerSymbol = new Text({ text: 'Matches', style: headerStyle });
    headerSymbol.x = -260;
    headerSymbol.y = yOffset;
    this.contentContainer.addChild(headerSymbol);
    
    const header3x = new Text({ text: '3', style: headerStyle });
    header3x.x = -80;
    header3x.y = yOffset;
    this.contentContainer.addChild(header3x);
    
    const header4x = new Text({ text: '4', style: headerStyle });
    header4x.x = 40;
    header4x.y = yOffset;
    this.contentContainer.addChild(header4x);
    
    const header5x = new Text({ text: '5', style: headerStyle });
    header5x.x = 160;
    header5x.y = yOffset;
    this.contentContainer.addChild(header5x);
    
    yOffset += 25;
    
    const symbols = getAllSymbols();
    
    // Load and display symbols with their multipliers (compact)
    for (let i = 0; i < Math.min(symbols.length, 8); i++) {
      const symbol = symbols[i];
      const symbolIndex = i + 1;
      
      // Load symbol image (smaller)
      try {
        const symbolTexture = await Assets.load(`/assets/symbols-2/${symbolIndex}.png`);
        const symbolSprite = new Sprite(symbolTexture);
        symbolSprite.scale.set(0.15); // 50% smaller than before (0.3 -> 0.15)
        symbolSprite.anchor.set(0, 0.5); // Center vertically
        symbolSprite.x = -260;
        symbolSprite.y = yOffset + 8;
        this.contentContainer.addChild(symbolSprite);
      } catch (error) {
        console.error(`Failed to load symbol ${symbolIndex}:`, error);
      }
      
      // Multipliers only (no symbol name)
      const mult3Text = new Text({ text: `${symbol.multipliers[2]}×`, style: multiplierStyle });
      mult3Text.x = -80;
      mult3Text.y = yOffset + 3;
      this.contentContainer.addChild(mult3Text);
      
      const mult4Text = new Text({ text: `${symbol.multipliers[3]}×`, style: multiplierStyle });
      mult4Text.x = 40;
      mult4Text.y = yOffset + 3;
      this.contentContainer.addChild(mult4Text);
      
      const mult5Text = new Text({ text: `${symbol.multipliers[4]}×`, style: multiplierStyle });
      mult5Text.x = 160;
      mult5Text.y = yOffset + 3;
      this.contentContainer.addChild(mult5Text);
      
      yOffset += 35;
    }
    
    yOffset += 15;
    
    // Active paylines info
    const paylinesInfo = new Text({ 
      text: `📊 ${GAME_CONFIG.PAYLINES.length} Active Paylines`,
      style: new TextStyle({
        fontFamily: 'Spenbeb, Georgia, serif',
        fontSize: 12,
        fontWeight: 'bold',
        fill: COLORS.TEXT_PRIMARY
      })
    });
    paylinesInfo.anchor.set(0.5);
    paylinesInfo.x = 0;
    paylinesInfo.y = yOffset;
    // this.contentContainer.addChild(paylinesInfo);
  }
  
  public open(): void {
    this.visible = true;
    this.isOpen = true;
    this.switchTab('howToPlay');
  }
  
  public close(): void {
    this.visible = false;
    this.isOpen = false;
    if (this.onCloseCallback) {
      this.onCloseCallback();
    }
  }
  
  public toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }
  
  public onClose(callback: () => void): void {
    this.onCloseCallback = callback;
  }
}